import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

/**
 * Scales of the rail, in pixels per minute. Roughly doubling steps, so a day fits in a few
 * screens at the coarsest and a five-second focus interval is readable at the finest.
 */
export const ZOOM_LEVELS: readonly number[] = [1, 2, 3, 6, 12, 24, 48, 96, 192];

/** A day is deliberately taller than the pane, so the rail scrolls rather than squeezing. */
export const DEFAULT_PX_PER_MINUTE = 3;

/**
 * Padding above the rail inside its scroll container, which the first gridline label needs to
 * sit in. It does not scale with the zoom, so {@link anchoredScrollTop} takes it off before
 * scaling and adds it back after. Applied as an inline style to keep it one measurement.
 */
export const RAIL_SCROLL_INSET_PX = 20;

const ZOOM_STORAGE_KEY = 'activity-timeline-px-per-minute';

export const readStoredPxPerMinute = () => {
  const stored: unknown = getLocalStorageItem(ZOOM_STORAGE_KEY);
  return typeof stored === 'number' && ZOOM_LEVELS.includes(stored)
    ? stored
    : DEFAULT_PX_PER_MINUTE;
};

export const storePxPerMinute = (pxPerMinute: number) =>
  setLocalStorageItem(ZOOM_STORAGE_KEY, pxPerMinute);

type ZoomAnchor = {
  /** Scroll offset of the rail's container before the zoom */
  scrollTop: number;
  /** Visible height of that container */
  viewportHeight: number;
  /** Padding above the rail inside that container — see {@link RAIL_SCROLL_INSET_PX} */
  insetPx: number;
  /** New pixels per minute over the old */
  scale: number;
};

/**
 * Scroll offset that leaves the time at the middle of the viewport still in the middle.
 *
 * Only the rail scales, so the offset is measured from the rail's top, stretched, and put back.
 * The result is deliberately not clamped to the zoomed content: a scroll container clamps an
 * offset past its own end, which is what should happen when the rail no longer fills the
 * viewport or the user was already parked at either end.
 */
export const anchoredScrollTop = ({
  scrollTop,
  viewportHeight,
  insetPx,
  scale,
}: ZoomAnchor) => {
  const viewportCentreInRail = scrollTop + viewportHeight / 2 - insetPx;
  return Math.max(
    0,
    insetPx + viewportCentreInRail * scale - viewportHeight / 2
  );
};
